import { Component, OnInit, ChangeDetectorRef, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RestrictionService } from 'src/services/restriction.service';
import { RestrictionReasonModel, RestrictionAuditHistoryModel, RestrictionModel, RestrictReasonType } from 'src/models/restriction.model';
import { DatePipe } from '@angular/common';
import { AuthStore } from 'src/app/store/authorization/auth.store';
import { Function } from 'src/Utilities/enum';
import { AppService } from 'src/app/app.service';
import { Subject } from 'rxjs';
import { ExcelExportService } from 'src/app/service/excel-export.service';
import { ExportEvent } from 'src/app/shared/dynamic-table/dynamic-table.model';
import { DateHelper, StringHelper } from 'src/Utilities/utility';
import { SHARED_IMPORTS } from 'src/app/shared/shared.module';
import { DynamicTableComponent } from 'src/app/shared/dynamic-table/dynamic-table.component';
import { SharedDatepickerComponent } from 'src/app/shared/shared-datepicker/shared-datepicker.component';
import { DefaultValues } from 'src/app/Constant';

@Component({
  selector: 'app-add-restriction',
  templateUrl: './add-restriction.component.html',
  styleUrls: ['./add-restriction.component.css'],
  standalone: true,
  imports: [...SHARED_IMPORTS,DynamicTableComponent,SharedDatepickerComponent]
})
export class AddRestrictionComponent implements OnInit, OnDestroy {
  stringHelper = StringHelper;
  restrictApplicantId: number = 0;
  restrictionDetails: RestrictionModel = {} as RestrictionModel;
  restrictionReasons: RestrictionReasonModel[] = [];
  auditData: RestrictionAuditHistoryModel[] = [];
  dynamicColumns: any[] = [];
  tableConfig: any;
  isAuditLoading: boolean = true;

  isSection1Collapsed: boolean = false;
  isSection2Collapsed: boolean = false;
  isSection3Collapsed = true;

  minDate: Date = new Date(1900, 0, 1);
  maxDate: Date = new Date();

  currentUserId: string;
  currentDate: string;
  dobChecked: boolean = true;
  dobTouched: boolean = false;
  newRestrictId: string;
  disableRowSelection: boolean = true;
  reasonTypeOptions: any[] = [];
  newRestrictionReason: RestrictionReasonModel = {} as RestrictionReasonModel;
  isReasonPushed: boolean = false;
  isMsgDisplayed: boolean = false;
  newRestrictionReasonTemp: RestrictionReasonModel = {} as RestrictionReasonModel;
  showValidationMessage: boolean = false;

  private destroy$ = new Subject<void>();
  private readonly authStore = inject(AuthStore);

  get gridEnabled(): boolean {
    return this.authStore.isVisible(Function.RestrictionAdd);
  }

  get restrictApplicantIdDisplay(): string {
    return this.restrictionDetails.RestrictApplicantId != null
      ? this.restrictionDetails.RestrictApplicantId.toString().padStart(6, '0')
      : '';
  }

  get isReasonDetailsInvalid(): boolean {
    const details = this.newRestrictionReason?.ReasonDetails;
    return !!details && (details.length < 10 || details.length > 500);
  }

  get isAddButtonDisabled(): boolean {
    return !this.gridEnabled ||
      this.isReasonDetailsInvalid ||
      !this.newRestrictionReason.ReasonTypeId ||
      !this.newRestrictionReason.ReasonDetails;
  }

  constructor(
    private route: ActivatedRoute,
    private restrictionService: RestrictionService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private exportExcel: ExcelExportService
  ) { }

  private deferUiUpdate(callback: () => void): void {
    setTimeout(() => {
      callback();
      this.cdr.markForCheck();
    }, 0);
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const id = params.get('restrictApplicantId');

      this.restrictApplicantId = +id;
      this.loadRestrictionData();
      this.loadAuditHistory();

      this.appService.currentUserId$.subscribe(id => {
        this.currentUserId = id;
      });

      let datePipe = new DatePipe('en-US');
      this.currentDate = datePipe.transform(new Date(), 'ddMMMyyyy') || '';

      this.setupAuditTrailTable();
      this.loadRestrictionReasonTypes();
    });
  }

  loadRestrictionData() {
    let datePipe = new DatePipe('en-US');

    this.restrictionService.getRestrictionDetails(this.restrictApplicantId).subscribe(response => {
      this.deferUiUpdate(() => {
        this.restrictionDetails = {
          RestrictApplicantId: response.RestrictApplicantId,
          ParticipantId: response.ParticipantId || 0,
          FirstName: response.FirstName || '',
          MiddleInitial: response.MiddleInitial || '',
          LastName: response.LastName || '',
          EmailId: response.EmailId || '',
          Phone: response.Phone || '',
          DOB: this.isMinDate(response.DOB) ? null : DateHelper.toDateOnlyDisplay(response.DOB),
          AppSuiteUnit: response.AppSuiteUnit || '',
          City: response.City || '',
          State: response.State || '',
          ZipCode: response.ZipCode || '',
          Country: (response.Country || '').trim() || 'USA',
          MailingAddress: response.MailingAddress || '',
          Participant_Name: (response.FirstName || '') + ' ' + (response.LastName || '')
        };

        // Load restriction reasons separately
        this.loadRestrictionReasons();
      });
    }, error => {
      console.error('Error loading restriction details:', error);
      alert('Failed to load restriction details. Please try again.');
    });
  }
  isMinDate(dob: string | null): any {
    const dobValue = dob ? new Date(dob) : null;
    const isMinDate = !dobValue || Number.isNaN(dobValue.getTime()) || dobValue.getFullYear() === 1;
    return isMinDate;
  }
  loadAuditHistory(): void {
    this.isAuditLoading = true;
    this.restrictionService.getRestrictionAuditHistory(this.restrictApplicantId).subscribe(
      (result: RestrictionAuditHistoryModel[]) => {
        this.deferUiUpdate(() => {
          this.auditData = Array.isArray(result) ? result : [];
          this.auditData.forEach(audit => {
            audit.ModifiedOn = audit.ModifiedOn ? this.stringHelper.changeToCSTFormat(new Date(audit.ModifiedOn)) : '';
          });
          this.isAuditLoading = false;
        });
      },
      (error) => {
        console.error('Error fetching audit history:', error);
        this.deferUiUpdate(() => {
          this.isAuditLoading = false;
        });
      }
    );
  }

  loadRestrictionReasons(): void {
    this.restrictionService.getRestrictionReasons(this.restrictApplicantId).subscribe(
      (result: RestrictionReasonModel[]) => {
        this.deferUiUpdate(() => {
          this.restrictionReasons = Array.isArray(result) ? result : [];
        });
      },
      (error) => {
        console.error('Error fetching restriction reasons:', error);
        this.deferUiUpdate(() => {
          this.restrictionReasons = [];
        });
      }
    );
  }

  loadRestrictionReasonTypes(): void {
    this.restrictionService.getRestrictionReasonTypes().subscribe(
      (result: RestrictReasonType[]) => {
        this.deferUiUpdate(() => {
          this.reasonTypeOptions = result.map(item => ({
            label: item.ReasonName,
            value: item.ReasonTypeId
          }));
        });
      },
      (error) => {
        console.error('Error fetching restriction reason types:', error);
      }
    );
  }

  addRestrictionReason(): void {
    const now = new Date();
    const restrictApplicantId = this.restrictionDetails.RestrictApplicantId === 0
      ? this.newRestrictionReason.RestrictApplicantId
      : this.restrictionDetails.RestrictApplicantId;

    const reasonToAdd: RestrictionReasonModel = {
      ...this.newRestrictionReason,
      CreatedBy: this.currentUserId,
      CreatedOn: now,
      ModifiedBy: this.currentUserId,
      ModifiedOn: now,
      RestrictApplicantId: restrictApplicantId,
      RestrictionDate: now
    };

    // Validate new restriction reason
    if (!reasonToAdd.ReasonTypeId || reasonToAdd.ReasonTypeId === 0) {
      alert('Please select a Reason Type.');
      return;
    }
    if (!reasonToAdd.ReasonDetails || reasonToAdd.ReasonDetails.length < 10) {
      alert('Reason Details must be at least 10 characters.');
      return;
    }
    if (reasonToAdd.ReasonDetails.length > 500) {
      alert('Reason Details cannot exceed 500 characters.');
      return;
    }

    const insertedReason: RestrictionReasonModel = { ...reasonToAdd };
    this.restrictionReasons = [insertedReason, ...this.restrictionReasons];
    this.cdr.markForCheck();

    if (!reasonToAdd.RestrictApplicantId || reasonToAdd.RestrictApplicantId === 0) {
      this.newRestrictionReasonTemp = { ...reasonToAdd };
      setTimeout(() => {
        this.newRestrictionReason = {} as RestrictionReasonModel;
      }, 0);
      return;
    }

    this.newRestrictionReason = reasonToAdd;
    // Save the new restriction reason
    this.restrictionService.saveRestrictionReasons(reasonToAdd).subscribe(
      response => {
        this.isReasonPushed = true;
        this.loadRestrictionReasons();
        setTimeout(() => {
          this.newRestrictionReason = {} as RestrictionReasonModel;
        }, 0);
        if (!this.isMsgDisplayed) {
          alert('Restriction reason added successfully.');
        }
      },
      error => {
        this.restrictionReasons = this.restrictionReasons.filter(reason => reason !== insertedReason);
        this.cdr.markForCheck();
        console.error('Error saving restriction reason:', error);
        alert('Failed to save restriction reason. Please try again.');
      }
    );
  }

  getReasonTypeName(reasonTypeId: number): string {
    const reasonType = this.reasonTypeOptions.find(opt => opt.value === reasonTypeId);
    return reasonType ? reasonType.label : '';
  }

  private validateSectionFields(): boolean {
    const rd = this.restrictionDetails || {} as RestrictionModel;
    if (
      !rd.FirstName ||
      !rd.MiddleInitial ||
      !rd.LastName ||
      !rd.DOB ||
      !rd.Phone ||
      !rd.EmailId ||
      !rd.MailingAddress ||
      !rd.City ||
      !rd.State ||
      !rd.ZipCode ||
      !rd.Country
    ) {
      return false;
    }
    if (this.isEmailInvalid()) {
      return false;
    }
    return true;
  }
  isEmailInvalid(): boolean {
    const email = this.restrictionDetails?.EmailId;
    return !!email && !/^[a-zA-Z0-9.\-_]+@[a-zA-Z.\-]+\.[a-zA-Z]{2,}$/.test(email);
  }

  saveRestrictionDetails() {
    this.showValidationMessage = false;

    if (!this.validateSectionFields()) {
      this.showValidationMessage = true;
      return;
    }

    // Sync DateOfBirth to DOB as date-only MM/DD/YYYY before saving
    this.restrictionDetails.DOB = DateHelper.toDateOnlyDisplay(
      this.restrictionDetails.DOB
    );

    // Set audit fields
    this.restrictionDetails.RestrictApplicantId = Number(this.newRestrictId || this.restrictApplicantId);
    this.restrictionDetails.ParticipantId = 1;
    this.restrictionDetails.CreatedBy = this.currentUserId;
    this.restrictionDetails.CreatedOn = new Date();
    this.restrictionDetails.ModifiedBy = this.currentUserId;
    this.restrictionDetails.ModifiedOn = new Date();

    let detailsToSave: RestrictionModel = { ...this.restrictionDetails };

    // Save restriction details only
    this.restrictionService.createOrUpdateRestriction(detailsToSave).subscribe(response => {
      if (this.newRestrictionReason.ReasonTypeId > 0 && this.newRestrictionReason.ReasonDetails !== ''
        && this.isReasonPushed == false) {
        this.newRestrictionReason.RestrictApplicantId = response;
      }

      this.loadAuditHistory();
      this.isMsgDisplayed = true;
      alert('Restriction details saved successfully.');
      this.router.navigate(['/adminhome/AddRestriction', response]);

    }, error => {
      console.error('Error saving restriction details:', error);
      alert('Failed to save restriction details. Please try again.');
    });
  }

  resetForm() {
    if (this.restrictApplicantId && this.restrictApplicantId > 0) {
      this.loadRestrictionData();
    }
    else {
      this.restrictionDetails = {} as RestrictionModel;
      this.restrictionDetails.Country = DefaultValues.USA;
    }
  }

  formatPhoneNumber(event: Event): void {
    let input = event.target as HTMLInputElement;
    let numbers = input.value.replace(/\D/g, '');
    if (numbers.length > 10) {
      numbers = numbers.substring(0, 10);
    }
    let formatted = '';
    if (numbers.length > 0) {
      formatted = numbers.substring(0, 3);
      if (numbers.length > 3) {
        formatted += '-' + numbers.substring(3, 6);
      }
      if (numbers.length > 6) {
        formatted += '-' + numbers.substring(6, 10);
      }
    }
    if (this.restrictionDetails) {
      this.restrictionDetails.Phone = formatted;
    }
    input.value = formatted;
  }

  onlyNumberKey(event: KeyboardEvent): boolean {
    let charCode = event.which ? event.which : event.keyCode;
    return charCode >= 48 && charCode <= 57;
  }

  alphaOnly(event: KeyboardEvent): void {
    let char = event.key;
    const input = event.target as HTMLInputElement;
    if (char === ' ' && (!input?.value || input.value.trim().length === 0)) {
      event.preventDefault();
      return;
    }
    if (!/^[a-zA-Z ]$/.test(char)) {
      event.preventDefault();
    }
  }

  middleInitialOnly(event: KeyboardEvent) {
    let char = event.key;
    if (!/^[a-zA-Z-]$/.test(char)) {
      event.preventDefault();
    }
  }

  setupAuditTrailTable() {
    this.tableConfig = {
      rows: 50,
      selectable: false,
      selectionMode: 'single',
      globalSearch: false,
      exportable: true,
      deletable: false,
      lazy: false,
      dataKey: 'RestrictionAudit',
      preferenceKey: 'RestrictionAudit',
      supportFilterHierarchy: false,
      title: '',
      columnFilteringEnabled: false,
      addable: false,
      showPagination: true
    };

    this.dynamicColumns = [
      { field: 'Action', header: 'Action Performed', width: '70px', sortable: false },
      { field: 'ModifiedOn', header: 'Modified On', width: '70px', sortable: false },
      { field: 'ModifiedBy', header: 'Modified By', width: '70px', sortable: false },
      { field: 'ColName', header: 'Field Name', width: '100px', sortable: false },
      { field: 'PrevValue', header: 'Previous Value', width: '150px', sortable: false },
      { field: 'NewValue', header: 'Updated Value', width: '150px', sortable: false },
    ];
  }

  // Dynamic table event handlers
  onDynamicFilterChange(event: any) { }
  onFilterOptionsRefresh(event: any) { }
  onDynamicExport(event: ExportEvent): void {
    this.exportAuditHistoryAsXLSX();
  }
  onDynamicPageChange(event: any) { }
  onDynamicSelectionChange(event: any) { }

  exportAuditHistoryAsXLSX(): void {
    let datePipe = new DatePipe('en-US');
    let auditExportData: any[] = [];

    if (this.auditData && this.auditData.length > 0) {
      this.auditData.forEach(audit => {
        auditExportData.push({
          'Action Performed': audit.Action || '',
          'Modified On': audit.ModifiedOn ? this.stringHelper.changeToCSTFormat(new Date(audit.ModifiedOn)) : audit.ModifiedOn,
          'Modified By': audit.ModifiedBy || '',
          'Field Name': audit.ColName || '',
          'Previous Value': audit.PrevValue || '',
          'Updated Value': audit.NewValue || ''
        });
      });

      const fileName = `Restriction_Audit_${this.restrictionDetails.FirstName}_${this.restrictionDetails.LastName}_${datePipe.transform(new Date(), 'yyyyMMdd')}`;
      this.exportExcel.exportAsExcelFile(auditExportData, fileName);
    } else {
      alert('No audit history data to export.');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
